import { useCallback } from "react";
import { useParams } from "react-router";
import { useFormik } from "formik";
import _ from "lodash";

import theme from "theme";
import { colors } from "theme/palette";
import { handleInvalidRequest } from "utils/sdk";

import Box from "components/Box";
import Button from "components/Button";
import RichTextEditorField from "components/RichTextEditorField";
import Stack from "components/Stack";
import Text from "components/Text";

import { useDealDetails } from "entities/Deal/sdk";
import { getConstructionTypeLabelByValue } from "entities/Deal/utils";
import MemoESignatureReminder from "entities/Memo/components/Sections/MemoESignatureDrawer/components/MemoESignatureReminder";
import {
  INITIAL_VALUES,
  INotificationEmailValues,
  VALIDATION_SCHEMA,
} from "entities/Memo/components/Sections/MemoESignatureDrawer/constants";
import { memoSignatureEmailNotificationProcessInitiate } from "entities/Memo/sdk";

export interface IMemoESignatureContentAssignmentOverview {
  navigateToPrintableMemoPage: () => void;
  companyName: string;
  memoName: string;
}

const MemoESignatureNotificationEmail = ({
  navigateToPrintableMemoPage,
  companyName,
  memoName,
}: IMemoESignatureContentAssignmentOverview) => {
  const params = useParams<{
    dealId: string;
    memoId: string;
  }>();
  const dealId = Number(params.dealId);
  const memoId = Number(params.memoId);

  const { data: deal } = useDealDetails({ dealId });

  const handleSubmit = useCallback(
    (values: INotificationEmailValues) =>
      memoSignatureEmailNotificationProcessInitiate({
        customTextAppendix: values.custom_text_appendix,
        intervalTime: values.days_interval_time,
        remindersEnabled: values.reminders_enabled,
        dealId,
        memoId,
      })
        .then(() => navigateToPrintableMemoPage())
        .catch(handleInvalidRequest),
    [dealId, memoId, navigateToPrintableMemoPage]
  );

  const formik = useFormik({
    initialValues: INITIAL_VALUES,
    validationSchema: VALIDATION_SCHEMA,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  const onCustomTextAppendixChange = useCallback(
    ({ value }: { value: string }) =>
      formik.setFieldValue("custom_text_appendix", value),
    [formik]
  );

  const onIntervalTimeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      formik.setFieldValue("days_interval_time", event.target.value);
    },
    [formik]
  );

  const onRemindersProcessStatusChange = useCallback(
    ({ remindersEnabled }: { remindersEnabled: boolean }) => {
      formik.setFieldValue("reminders_enabled", remindersEnabled);
    },
    [formik]
  );

  return (
    <Stack
      sx={{
        flex: 1,
        padding: 3,
      }}
      gap={5}
    >
      <Stack
        alignItems="flex-start"
        sx={{
          padding: theme.spacing(1.5, 1.5),
          backgroundColor: colors.gray10,
          borderRadius: "8px",
        }}
      >
        <Text variant="text2" fontWeight={500}>
          A notification email will be sent to each signatory person.
        </Text>
        <Text variant="text4" color={colors.gray80}>
          A customized message can be added to the end of the email where you
          can include your name as the sender or any additional request to the
          recipient.
        </Text>
      </Stack>
      <Stack gap={2.5} padding={theme.spacing(0, 1.5)} flex={1}>
        <Stack direction="row" gap={1.5}>
          <Box
            sx={{
              height: "40px",
              width: "8px",
              backgroundColor: colors.blue100,
            }}
          />
          <Stack>
            <Text variant="labelSmall" color={colors.gray60} fontWeight={500}>
              email subject
            </Text>
            <Text variant="text2" fontWeight={700}>
              E-sign request from {companyName}
            </Text>
          </Stack>
        </Stack>
        <Text variant="text2" fontWeight={400}>
          {companyName} requests you to review and sign their{" "}
          <Text component="span" variant="text2" fontWeight={700}>
            {memoName}
          </Text>{" "}
          document.
        </Text>
        <Stack>
          <ul
            style={{
              margin: 0,
              listStyleType: "none",
              padding: 0,
            }}
          >
            <li>
              <Text variant="text2" fontWeight={400}>
                Deal context:
              </Text>
            </li>
            <li>
              <Text variant="text2" fontWeight={700}>
                {deal?.name}
              </Text>
            </li>
            <li>
              <Text variant="text2" fontWeight={400}>
                {deal?.address.street_address}, {deal?.address.city},{" "}
                {deal?.address.state} {deal?.address.zipcode}
              </Text>
            </li>
            <li>
              {!_.isNil(deal) && (
                <Text variant="text2" fontWeight={400}>
                  {getConstructionTypeLabelByValue({
                    value: deal?.construction_type,
                  })}
                </Text>
              )}
            </li>
          </ul>
        </Stack>
        <Stack>
          <Text variant="text2" fontWeight={400}>
            Access and sign the document through this link:
          </Text>
          <Text variant="text2" fontWeight={700} color={colors.blue100}>
            link will be inserted here
          </Text>
        </Stack>
        <Stack flex={1}>
          <RichTextEditorField
            {...formik.getFieldProps("custom_text_appendix")}
            onChange={(value) => onCustomTextAppendixChange({ value })}
            sx={{ flex: 1 }}
          />
        </Stack>
        <MemoESignatureReminder
          formik={formik}
          onIntervalTimeChange={onIntervalTimeChange}
          onRemindersProcessStatusChange={onRemindersProcessStatusChange}
        />
      </Stack>
      <Button onClick={formik.submitForm}>Next step</Button>
    </Stack>
  );
};

export default MemoESignatureNotificationEmail;
